import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as API from "../Endpoints/Endpoints";
import jsPDF from 'jspdf';
import { FaDownload, FaEye, FaTimes } from 'react-icons/fa';

function ViewPatentBot() {
  const [bot, setChatbots] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    const fetchbot = async () => {
      try {
        const response = await axios.get(API.GET_PATENT_API);
        setChatbots(response.data || []);
      } catch (error) {
        console.error('Error fetching patents:', error);
        toast.error('Failed to fetch patent records');
      }
    };
    fetchbot();
  }, []);

  useEffect(() => {
    if (viewOpen && modalRef.current) {
      setTimeout(() => {
        try { modalRef.current.scrollTop = 0; } catch (e) {}
      }, 0);
    }
  }, [viewOpen]);

  const refreshList = async () => {
    try {
      const res = await axios.get(API.GET_PATENT_API);
      setChatbots(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const filteredBots = bot.filter((item) => {
    const q = (searchQuery || '').toLowerCase();
    return (
      item.useremail?.toLowerCase().includes(q) ||
      item.organizationname?.toLowerCase().includes(q) ||
      item.patentdescription?.toLowerCase().includes(q) ||
      item.patentnumber?.toLowerCase().includes(q) ||
      item.inventors?.toLowerCase().includes(q) ||
      item.patentvaluechain?.toLowerCase().includes(q) ||
      item.patenttechnology?.toLowerCase().includes(q) ||
      item.patentrelatedterms?.toLowerCase().includes(q)
    );
  });

  const handleView = async (item) => {
    try {
      setViewLoading(true);
      setViewOpen(true);
      setSelected(null);

      const res = await axios.get(API.GET_PATENT_API);
      const rec = (res.data || []).find((r) => r.patentid === item.patentid);
      if (!rec) {
        toast.error('Record not found');
        setViewOpen(false);
        return;
      }
      setSelected(rec);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load details');
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleImport = (item) => {
    const doc = new jsPDF();
    let yPosition = 20;
    const lineHeight = 10;
    const maxWidth = 180;
    const pageHeight = doc.internal.pageSize.height;

    const safeText = (t) => String(t ?? '');

    const addText = (text, x, y) => {
      const splitText = doc.splitTextToSize(safeText(text), maxWidth);
      doc.text(splitText, x, y);
      return splitText.length * lineHeight;
    };

    const checkAndAddPage = (currentYPosition) => {
      if (currentYPosition + lineHeight > pageHeight) {
        doc.addPage();
        return 20;
      }
      return currentYPosition;
    };

    const addHeading = (heading, x, y) => {
      doc.setFont('helvetica', 'bold');
      y += addText(heading, x, y);
      doc.setFont('helvetica', 'normal');
      return y;
    };

    yPosition = addHeading(`Organization name:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.organizationname, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`User email:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.useremail, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Description:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.patentdescription, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Patent Number & Claims:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.patentnumber, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Inventors:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.inventors, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Patent Value Chain:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.patentvaluechain, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Patent Technology:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.patenttechnology, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Related Terms:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.patentrelatedterms, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    const filename = (item.patentnumber || item.patentdescription || 'patent').replace(/\s+/g, '_');
    doc.save(`${filename}.pdf`);
  };

  return (
    <div className="container mx-auto px-4 mt-10">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Patent References</h1>
        <input
          type="text"
          placeholder="Search..."
          className="px-4 py-2 border rounded-md"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border-gray-300 shadow-md rounded-md table-fixed">
          <colgroup>
            <col style={{ width: '7%' }} />
            <col style={{ width: '4%' }} />
            <col />
          </colgroup>

          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Sr No</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Title / Patent Number</th>
            </tr>
          </thead>

          <tbody>
            {filteredBots.length > 0 ? (
              filteredBots.map((item, index) => (
                <tr key={item.patentid || index} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-normal">
                    <div className="flex items-center gap-2">
                      <button
                        className="text-indigo-600 hover:underline flex items-center gap-1"
                        onClick={() => handleView(item)}
                        title="View"
                      >
                        <FaEye />
                      </button>

                      <button
                        className="text-blue-500 hover:underline flex items-center gap-1"
                        onClick={() => handleImport(item)}
                        title="Export as PDF"
                      >
                        <FaDownload />
                      </button>

                      {/* Uncomment and wire your edit/delete components:
                          <EdituserPatent patentid={item.patentid} refreshList={refreshList} />
                          <DeleteuserPatent patentid={item.patentid} refreshList={refreshList} />
                      */}
                    </div>
                  </td>

                  <td className="px-4 py-3 whitespace-normal text-sm">{index + 1}</td>

                  <td className="px-4 py-3 whitespace-normal text-sm break-words max-w-[640px]">
                    {/* <div className="font-semibold">{item.patentnumber || '—'}</div> */}
                    <div className="text-sm text-gray-700 mt-1">{item.patentdescription}</div>
                    {/* <div className="text-xs text-gray-500 mt-2">
                      {item.inventors ? `Inventors: ${item.inventors}` : ''}
                    </div> */}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="px-6 py-8 text-center text-gray-500">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* VIEW MODAL */}
      {viewOpen && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 p-4">
          <div
            ref={modalRef}
            className="bg-white w-full max-w-4xl rounded-2xl shadow-xl p-6 relative overflow-y-auto max-h-[90vh]"
          >
            <button
              className="absolute top-4 right-4 text-gray-600 hover:text-black"
              onClick={() => { setViewOpen(false); setSelected(null); }}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            {viewLoading ? (
              <div className="py-20 text-center">Loading...</div>
            ) : selected ? (
              <>
                <div className="mb-2 text-2xl font-semibold break-words">
                  {selected.patentnumber || selected.patentdescription || 'Untitled Patent'}
                </div>
                <div className="text-sm text-gray-600 mb-4">
                  {selected.useremail ? `By ${selected.useremail}` : ''} {selected.organizationname ? `• ${selected.organizationname}` : ''}
                </div>

                <PdfSection title="Description" text={selected.patentdescription} />
                <PdfSection title="Patent Number & Claims" text={selected.patentnumber} />
                <PdfSection title="Inventors" text={selected.inventors} />
                <PdfSection title="Patent Value Chain" text={selected.patentvaluechain} />
                <PdfSection title="Patent Technology" text={selected.patenttechnology} />
                <PdfSection title="Related Terms" text={selected.patentrelatedterms} />

                <div className="mt-6 flex justify-end">
                  <button
                    className="px-6 py-2 bg-gray-600 text-white rounded-lg shadow hover:bg-gray-700"
                    onClick={() => { setViewOpen(false); setSelected(null); }}
                  >
                    Back
                  </button>
                </div>
              </>
            ) : (
              <div className="py-20 text-center text-red-600">No record found.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PdfSection({ title, text }) {
  if (!text) return null;
  return (
    <div className="mt-4">
      <h3 className="text-lg font-semibold border-b border-gray-200 pb-1">{title}</h3>
      <div className="mt-2 whitespace-pre-wrap leading-relaxed text-gray-800 break-words">
        {text}
      </div>
    </div>
  );
}

export default ViewPatentBot;
